import React, {
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { createContext } from "react";

import { ElectionContext } from "../election.context";
import { Loading } from "../../../components/loading";
import { IOptInResponse, IOptInConfig } from "../../../domain/ballot-opt-in";
import { useStoredJson } from "../../../context/localstorage.provider";
import { IBallotState, IQuestionState, IQuestionStateMap } from "../../../domain/ballot-state";
import { prepareSubmitMux } from "../../../question-types/prepare-mux";
import { initialValueMux } from "../../../question-types/initial-value-mux";
import { IVoter } from "../../../domain/voter";
import { ApiBallot, ApiIQuestion, ISubmitState } from "../../../domain/election";
import { sortDisplayOrder } from "../util";
type QuestionRespponses = { [question_id: string]: IQuestionState };


export interface SubmitResult {
  success: boolean;
  message?: string;
  code: string;
}

export interface IBallotContext {
  ballot: ApiBallot;
  questionStates: QuestionRespponses;
  setQuestionState: (
    question_id: string,
    value: React.SetStateAction<IQuestionState>
  ) => void;
  setOptIn: (optInResponse: IOptInResponse) => void;
  opt_in?: boolean;
  opt_in_details?: any;
  questions: Array<ApiIQuestion>;
  ballotOptIn: IOptInConfig | null;
  ballotState: IBallotState;
  submitState: ISubmitState;
  valid: boolean;
  prepareForReview: () => void;
  setProxyVotes: (updateProxies: SetStateAction<string[]>) => void;
  proxyVotes?: string[];
  clearBallotState: () => void;
}

export const BallotContext = createContext<IBallotContext>({
  ballot: {} as any,
  questionStates: {},
  setQuestionState: (
    question_id: string,
    value: React.SetStateAction<IQuestionState>
  ) => null,
  setOptIn: (optInResponse: IOptInResponse) => null,
  questions: [],
  ballotOptIn: null,
  ballotState: {} as any,
  submitState: {} as any,
  valid: false,
  prepareForReview: () => null,
  setProxyVotes: () => null,
  clearBallotState: () => null,
});
interface BallotProviderProps extends PropsWithChildren {
  ballot: ApiBallot;
}
export const BallotProvider = ({
  children,
  ballot,
}: BallotProviderProps) => {
  // const { setBallotResponse, ballotState } = useContext(ElectionContext);
  // const ballotState: IBallotState | undefined =
  //   ballotState[ballot.id];
  const { voter, submitStateMap } = useContext(ElectionContext);
  const submitState = submitStateMap[ballot.id];

  const [ballotState, setBallotState, clearBallotState] = useInternalBallotState(ballot, voter)


  const questions = useMemo(
    () => ballot.questions.sort(sortDisplayOrder),
    [ballot]
  );

  const setProxyVotes = useCallback((updateProxies: SetStateAction<string[]>) => {
    setBallotState(state => {

      const proxy_votes = typeof updateProxies === 'function' ? updateProxies(state.proxy_votes || []) : updateProxies;

      return {
        ...state,
        proxy_votes
      }
    });
  }, [setBallotState])

  const resolveQuestion = useCallback(
    (question: string | ApiIQuestion) => {
      if (typeof question === "object") {
        return question;
      }
      if (typeof question === "string" && questions) {
        const b = questions.find((b) => b.id === question);
        if (b) {
          return b;
        }
      }
      return null;
    },
    [questions]
  );

  const setQuestionState = useCallback(
    (questionId: string, value: React.SetStateAction<IQuestionState>) => {
      const _question = resolveQuestion(questionId);
      if (!_question) {
        throw new Error("Must have ballot");
      }
      setBallotState((ballotState): IBallotState => {
        const current = ballotState.question_states[questionId] || {
          question_id: questionId,
          type: _question.response_type,
        };

        const response: IQuestionState =
          typeof value === "function" ? value(current) : value;

        return {
          ...ballotState,
          question_states: {
            ...ballotState.question_states,
            [questionId]: response,
          },
        };
      });
    },
    [setBallotState, resolveQuestion]
  );
  const handleSetOptIn = useCallback((value: IOptInResponse) => {
    setBallotState((ballotState): IBallotState => {
      return {
        ...ballotState,
        opt_in: value.opt_in,
        opt_in_details: value.opt_in_details
      };
    });
  }, [setBallotState]);


  const valid = useMemo(() => {
    if (!ballotState) {
      return false;
    }
    if (ballot.opt_in_config) {
      if (ballotState.opt_in === false) {
        return true
      }
    }
    for (const question of ballot.questions) {
      const questionState = ballotState.question_states[question.id];

      if (question.required && (!questionState || !questionState.value)) {
        if (!questionState) {
          return false;
        }
      }

      const ballotValid = questionState.value && questionState.valid

      if (!ballotValid) {
        return false;
      }
    }
    return true;
  }, [ballot, ballotState])

  const prepareForReview = useCallback(() => {
    setBallotState((ballotState): IBallotState => {
      const question_states: { [question_id: string]: IQuestionState<any> } = {};

      for (const questionState of Object.values(ballotState.question_states)) {
        const question = resolveQuestion(questionState.question_id);
        if (question) {
          question_states[question.id] = {
            ...questionState,
            dirty: true,
            value: prepareSubmitMux(question, questionState)
          };
        }
      }

      return {
        ...ballotState,
        question_states: question_states,
      };
    });

  }, [setBallotState, resolveQuestion])


  if (!questions) {
    return <Loading source="BallotProvider" />;
  }

  if (!ballotState) {
    return <Loading source="ballotState" />;
  }

  return (
    <BallotContext.Provider
      value={{
        ballot,
        questions: questions,
        questionStates: ballotState.question_states || {},
        opt_in: ballotState.opt_in,
        opt_in_details: ballotState.opt_in_details,
        submitState,
        setQuestionState,
        setOptIn: handleSetOptIn,
        ballotOptIn: null,
        ballotState: ballotState,
        valid,
        prepareForReview,
        setProxyVotes,
        proxyVotes: ballotState.proxy_votes,
        clearBallotState
      }}
    >
      {children}
    </BallotContext.Provider>
  );
};


const useInternalBallotState = (ballot: ApiBallot, voter: IVoter): [IBallotState,
  (v: React.SetStateAction<IBallotState>) => void,
  () => void
] => {

  const defaultState = useMemo(() => ({
    ballot_id: ballot.id,
    question_states:
      ballot.questions.reduce(
        (prev: IQuestionStateMap, question: ApiIQuestion): IQuestionStateMap => {
          return {
            ...prev,
            [question.id]: {
              question_id: question.id,
              dirty: false,
              response_type: question.response_type,
              data_version: "",
              valid: false,
              value: initialValueMux(question)
            }
          }
        },
        {} as IQuestionStateMap
      )
  }), [ballot]);

  const [ballotState, setBallotState] = useStoredJson<IBallotState | null>(
    `response_${voter.voting_round_id}_${voter.id}_${ballot.id}`,
    defaultState
  );

  const _ballotState = useMemo(() => {
    if (ballotState) {
      return ballotState;
    }

    return defaultState;
  }, [ballotState])

  const _setBallotState = useCallback((value: React.SetStateAction<IBallotState>) => {
    setBallotState(v => {
      if (typeof value === "function") {
        return value(v || defaultState)
      }
      return value;
    });
  }, [setBallotState]);
  const clearBallotState = useCallback(() => {
    setBallotState(defaultState);
  }, [setBallotState]);
  return [_ballotState, _setBallotState, clearBallotState];
}